import {
  Button,
  Form,
  Heading,
  popSuccessToast,
  TextField,
  Banner,
  popErrorToast,
  convertDisplayDateToISO,
  formatPhoneToE164
} from '@boltenergy-be/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import styles from './PersonalForm.module.scss'
import Phone from 'components/form-fields/phone/Phone'
import { useForm, FormProvider } from 'react-hook-form'
import { PersonalFormInputs } from '../types'
import { updateAccount, updateContact } from 'store/contact/thunks'
import dayjs from 'dayjs'
import { routes } from 'types/routes'
import Link from 'components/link/Link'
import { useNavigate } from 'react-router'
import { UserTypes } from 'store/auth/types'
import { useEffect } from 'react'
import { selectCurrentAccount } from 'store/contact/slice.ts'
import { DATE_FORMAT } from 'constants/constants.ts'
import BirthDate from 'components/form-fields/birth-date/BirthDate.tsx'
import { IS_PROD_TESTING } from 'constants/envs.ts'

const PersonalForm = () => {
  //i18n
  const { t } = useTranslation(['user', 'common', 'contracts', 'validation'])

  // Router
  const navigate = useNavigate()

  // Redux
  const dispatch = useStoreDispatch()
  const { contact, loading } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)
  const { contactSfId, sfId } = account
  const { userType } = useStoreSelector((store) => store.auth)

  // React hook form

  const hookForm = useForm<PersonalFormInputs>({
    mode: 'onBlur',
    defaultValues: {
      phone: contact?.phone ? formatPhoneToE164(contact.phone) : '',
      birthDate: account.birthDate ? dayjs(account.birthDate).format(DATE_FORMAT) : ''
    }
  })
  const {
    handleSubmit,
    formState: { errors, dirtyFields }
  } = hookForm

  /**
   * Redirect to Personal page if not super user
   */
  useEffect(() => {
    if (userType !== UserTypes.SUPER_USER) {
      void navigate(routes.USER_PERSONAL)
    }
  }, [userType, navigate])

  /**
   * Handles the form submission after validation by React Hook Form
   *
   * @param {PersonalFormInputs} data
   */
  const onSubmit = async (data: PersonalFormInputs) => {
    // Update the account (only map the fields that have changed since it's a PATCH request)
    let resAccount
    let resContact

    //update account fields
    if (dirtyFields.birthDate) {
      resAccount = await dispatch(
        updateAccount({
          accountId: sfId,
          contactId: contactSfId,
          account: {
            birthDate: convertDisplayDateToISO(data.birthDate)
          }
        })
      )
    }

    //update contact fields
    if (dirtyFields.phone) {
      resContact = await dispatch(
        updateContact({
          contactId: contactSfId,
          contact: {
            phone: data.phone
          }
        })
      )
    }

    if (dirtyFields.birthDate && dirtyFields.phone) {
      if (resAccount?.meta.requestStatus === 'fulfilled' && resContact?.meta.requestStatus === 'fulfilled') {
        void navigate(routes.USER_PERSONAL)
        popSuccessToast(
          t(($) => $.formFeedback.saved, {
            ns: 'common'
          })
        )
      } else {
        popErrorToast(
          t(($) => $.formFeedback.error, {
            ns: 'common'
          })
        )
      }
    } else {
      if (resAccount?.meta.requestStatus === 'fulfilled' || resContact?.meta.requestStatus === 'fulfilled') {
        void navigate(routes.USER_PERSONAL)
        popSuccessToast(
          t(($) => $.formFeedback.saved, {
            ns: 'common'
          })
        )
      } else {
        popErrorToast(
          t(($) => $.formFeedback.error, {
            ns: 'common'
          })
        )
      }
    }
  }

  return (
    <FormProvider {...hookForm}>
      <Form className={styles['personal-form']} onSubmit={handleSubmit(onSubmit)}>
        <Banner type="informative">
          <Trans
            t={t}
            i18nKey={($) => $.personal.form.info}
            ns="user"
            components={[<Link key="link" href={routes.SUPPORT_CONTACT} variant="inline" />]}
          />
        </Banner>

        <Heading as="h2" variant="h5" className="mt-500">
          {t(($) => $.personal, {
            ns: 'common'
          })}
        </Heading>

        {/*Customer Number*/}
        <TextField
          id="customerNumber"
          label={t(($) => $.personal.form.clientNbr)}
          type="text"
          name="customerNumber"
          defaultValue={account.customerNumber}
          disabled
          grid="full"
        />

        {/*First Name*/}
        <TextField
          id="firstName"
          label={t(($) => $.personal.form.firstName)}
          type="text"
          name="firstName"
          defaultValue={contact?.firstName || ''}
          disabled
          grid="full"
        />

        {/*Last Name*/}
        <TextField
          id="lastName"
          label={t(($) => $.personal.form.lastName)}
          type="text"
          name="lastName"
          defaultValue={contact?.lastName || ''}
          disabled
          grid="full"
        />

        {/*Email*/}
        <TextField
          id="email"
          label={t(($) => $.personal.form.email)}
          type="email"
          name="email"
          defaultValue={contact?.email || ''}
          disabled
          grid="full"
        />

        {/*Phone*/}
        <Phone label={t(($) => $.personal.form.phone)} hookFormIdentifier="phone" />

        {/*Birth Date*/}
        <BirthDate />

        {!!account.company && (
          <>
            <Heading as="h2" variant="h5">
              {t(($) => $.add.steps.customerData.sections.company, {
                ns: 'contracts'
              })}
            </Heading>

            {/*Company Name*/}
            <TextField
              id="companyName"
              label={t(($) => $.personal.form.company.name)}
              type="text"
              name="companyName"
              defaultValue={account.company.name}
              disabled
              grid="full"
            />

            {/*Legal Form*/}
            <TextField
              id="companyLegalForm"
              label={t(($) => $.personal.form.company.legalForm)}
              type="text"
              name="companyLegalForm"
              defaultValue={account.company.legalForm}
              disabled
              grid="full"
            />

            {/*Enterprise Number*/}
            <TextField
              id="companyEnterpriseNumber"
              label={t(($) => $.personal.form.company.enterpriseNumber)}
              type="text"
              name="companyEnterpriseNumber"
              defaultValue={account.company.enterpriseNumber}
              disabled
              grid="full"
            />
          </>
        )}

        <Button
          isFullwidth
          type="submit"
          disabled={IS_PROD_TESTING || Object.keys(errors).length > 0 || userType !== UserTypes.SUPER_USER}
          loading={loading}
        >
          {t(($) => $.save, {
            ns: 'common'
          })}
        </Button>
      </Form>
    </FormProvider>
  )
}

export default PersonalForm
